import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../data/business';

interface SeoProps {
  /** Full <title>. One unique title per indexable page. */
  title: string;
  /** Meta description. One unique description per indexable page. */
  description: string;
  /** Path only, e.g. "/dishes/khao-soi". Becomes the self-referencing canonical. */
  path: string;
  /** Absolute or root-relative image for social cards. */
  image?: string;
  ogType?: 'website' | 'article' | 'restaurant.restaurant';
  /** JSON-LD objects. Emitted one <script> per entity. */
  schemas?: object[];
  /** Set on pages that should stay out of the index. */
  noindex?: boolean;
}

const DEFAULT_IMAGE = '/assets/images/optimized/ThaprachanLogo.webp';

const absolute = (pathOrUrl: string) =>
  pathOrUrl.startsWith('http') ? pathOrUrl : `${SITE_URL}${pathOrUrl}`;

/**
 * Every indexable page renders exactly one of these. It guarantees the four
 * things that were missing site-wide: a unique title/description pair, a
 * self-referencing canonical, complete social-card metadata with absolute URLs,
 * and structured data that points at the single business entity.
 */
const Seo = ({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  ogType = 'website',
  schemas = [],
  noindex = false,
}: SeoProps) => {
  const canonical = absolute(path);
  const imageUrl = absolute(image);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      <meta property="og:site_name" content="Thaprachan Thai Cuisine" />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {schemas.map((schema, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
